import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  Select,
  DataTable,
  Dropdown,
  Skeleton,
  Pagination,
  EmptyState,
  ErrorBoundary,
  StockLevelIndicator,
  Drawer,
  Modal,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { productMasterService } from '@/services/product-master.service';
import { locationService } from '@/services/location.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { STOCK_STATUS_OPTIONS, ROLE_LEVELS } from '@/constants/product';
import { GLASS, GLASS_INTERACTIVE, GLASS_FILTER, cn } from '@/constants/design-tokens';
import { formatPaise, formatNumber } from '@/utils/currency';
import { escapeField } from '@/features/reports/csv';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { apiErrorMessage } from '@/lib/api-error-message';
import { useCategoryFilterOptions, useCategoryMasterNames } from '@/components/CategoryCombobox';
import type { CurrencyCode } from '@bbpos/types';
import type { Product } from '@/types/product';
import { ReorderModal } from './products/ReorderModal';
import { makeMasterColumns } from './product-masters/master-columns';
import { masterProductHref } from './product-masters/MasterCataloguePage';
import type { ProductMasterListItem } from '@/types/product-master';

type ProductRow = Product & { id: string };

// ─── Icons ──────────────────────────────────────────────────────────────────

function PackageIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path strokeLinecap="round" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function buildProductListApiParams(searchParams: URLSearchParams, fallbackLocationId?: string) {
  const statusFilter = searchParams.get('status') ?? '';
  const locationFilter = searchParams.get('location') ?? '';
  return {
    page: Number(searchParams.get('page') ?? '1'),
    limit: 20,
    search: searchParams.get('search') || undefined,
    category: searchParams.get('category') || undefined,
    stock_status: statusFilter || undefined,
    location_id: locationFilter || fallbackLocationId,
    sort: searchParams.get('sort') || undefined,
    order: searchParams.get('order') || undefined,
  };
}

// The list query is server-paginated (20 rows a page, server cap 100), so an
// export built from `data.data` only ever held the current page. Walk every
// page of the SAME filtered query so the file is the whole catalogue as the
// user has narrowed it, not whatever happens to be on screen.
export const EXPORT_PAGE_SIZE = 100;

type ExportListParams = Omit<Parameters<typeof productService.list>[0], 'page' | 'limit'>;
type ExportListFn = (
  params: ExportListParams & { page: number; limit: number },
) => Promise<{ data: ProductRow[]; meta: { totalPages: number } }>;

export async function fetchAllProductsForExport(
  list: ExportListFn,
  params: ExportListParams,
): Promise<ProductRow[]> {
  const rows: ProductRow[] = [];
  let page = 1;
  let totalPages = 1;
  do {
    const res = await list({ ...params, page, limit: EXPORT_PAGE_SIZE });
    rows.push(...res.data);
    totalPages = res.meta?.totalPages ?? 1;
    page += 1;
  } while (page <= totalPages);
  return rows;
}

// QA 3.5.16: fields are CSV-escaped — a comma in a name (or in a formatted price
// like ₹1,234.00) used to shift every later column one place right.
export function buildProductExportCsv(all: ProductRow[], currency: CurrencyCode): string {
  const header = 'Name,SKU,Category,Price,Stock,Unit,Status,Hidden in POS,Hidden in Sales\n';
  const rows = all
    .map((p) =>
      [p.name, p.sku, p.category ?? '', formatPaise(p.price, currency), p.stock_quantity, p.unit, p.is_active ? 'Active' : 'Inactive', p.hide_in_pos ? 'Yes' : 'No', p.hide_in_sales ? 'Yes' : 'No']
        .map(escapeField)
        .join(','),
    )
    .join('\n');
  return header + rows;
}

// QA 3.5.17: bulk actions fire one request per product, so some can fail while
// others land. Settle every request and report which ids failed instead of
// letting one rejection (or a blanket success toast) hide the partial outcome.
export async function runBulkProductUpdate(
  ids: string[],
  update: (id: string) => Promise<unknown>,
): Promise<{ succeeded: string[]; failed: string[]; firstError?: unknown }> {
  const results = await Promise.allSettled(ids.map((id) => update(id)));
  const succeeded: string[] = [];
  const failed: string[] = [];
  results.forEach((r, i) => (r.status === 'fulfilled' ? succeeded : failed).push(ids[i]));
  const firstError = results.find((r): r is PromiseRejectedResult => r.status === 'rejected')?.reason;
  return { succeeded, failed, firstError };
}

function getUserRoleLevel(role?: string): number {
  return ROLE_LEVELS[role ?? ''] ?? 0;
}

// Variant filter — `has_variants` comes from the list query, which flags a
// parent that owns at least one active variant.
// Column headers cover name/category/price/stock; this exposes the orderings
// that have no column of their own.
const SORT_OPTIONS = [
  { value: '', label: 'Sort: Name' },
  { value: 'created_at:desc', label: 'Recently Added' },
  { value: 'stock_quantity:desc', label: 'Stock: High to Low' },
  { value: 'price:desc', label: 'Price: High to Low' },
];

/** Master list column key -> the sort name both lists' API takes (C13). */
const MASTER_SORT_KEY: Record<string, string> = {
  default_price: 'price',
  total_stock: 'stock_quantity',
};

const PRODUCT_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'GOODS', label: 'Goods' },
  { value: 'SERVICE', label: 'Service' },
];

const VARIANT_FILTER_OPTIONS = [
  { value: '', label: 'All products' },
  { value: 'with', label: 'Has variants' },
  { value: 'without', label: 'No variants' },
];

// ─── Table columns ───────────────────────────────────────��──────────────────

function makeColumns(
  currency: CurrencyCode,
  onOpen: (row: ProductRow) => void,
): ColumnDef<ProductRow>[] {
  return [
  {
    key: 'name',
    header: 'Product',
    sortable: true,
    render: (_v, row) => (
      <div className="flex items-center gap-3">
        {row.image_url ? (
          <img
            src={row.image_url}
            alt={row.name}
            className="h-10 w-10 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-teal-100 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/20">
            <PackageIcon className="h-5 w-5 text-teal-500 dark:text-teal-400" />
          </div>
        )}
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => onOpen(row)}
            className="text-left text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate hover:text-teal-600 dark:hover:text-teal-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 rounded"
          >
            {row.name}
          </button>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 tabular-nums">{row.sku}</p>
        </div>
      </div>
    ),
  },
  {
    key: 'brand',
    header: 'Brand',
    sortable: false,
    render: (_v, row) => (
      <span className="text-sm text-neutral-600 dark:text-neutral-300">{row.brand ?? '—'}</span>
    ),
  },
  {
    key: 'category',
    header: 'Category',
    sortable: true,
    render: (_v, row) => (
      <span className="text-sm text-neutral-600 dark:text-neutral-300">{row.category ?? '—'}</span>
    ),
  },
  {
    key: 'has_variants',
    header: 'Variants',
    render: (_v, row) =>
      row.has_variants ? (
        <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
          Variants
        </span>
      ) : row.parent_product_id ? (
        <span className="text-xs text-neutral-400 dark:text-neutral-500">Variant</span>
      ) : (
        <span className="text-xs text-neutral-300 dark:text-neutral-600">—</span>
      ),
  },
  {
    key: 'price',
    header: 'Price',
    numeric: true,
    sortable: true,
    render: (_v, row) => (
      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
        {formatPaise(row.price, currency)}
      </span>
    ),
  },
  {
    key: 'stock_quantity',
    header: 'Stock',
    numeric: true,
    sortable: true,
    render: (_v, row) => (
      <div className="flex items-center gap-2">
        <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">
          {formatNumber(row.stock_quantity)} {row.unit}
        </span>
        <StockLevelIndicator
          currentStock={row.stock_quantity}
          reorderLevel={row.low_stock_threshold ?? undefined}
          variant="dot"
          size="sm"
        />
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => (
      <StockLevelIndicator
        currentStock={row.stock_quantity}
        reorderLevel={row.low_stock_threshold ?? undefined}
        variant="badge"
        size="sm"
      />
    ),
  },
  {
    key: 'is_active',
    header: 'Active',
    // A draft is an unfinished wizard entry, not a product someone chose to
    // deactivate — badge it separately so the two are not confused.
    render: (_v, row) => (row.is_draft ? (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
        <span className="h-2 w-2 rounded-full bg-amber-500" />
        Draft
      </span>
    ) : (
      <span className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium',
        row.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-400 dark:text-neutral-500',
      )}>
        <span className={cn(
          'h-2 w-2 rounded-full',
          row.is_active ? 'bg-emerald-500' : 'bg-neutral-300 dark:bg-neutral-600',
        )} />
        {row.is_active ? 'Active' : 'Inactive'}
        {row.hide_in_pos && (
          <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300" title="Hidden from counter billing / restaurant ordering">
            Hidden: POS
          </span>
        )}
        {row.hide_in_sales && (
          <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300" title="Hidden from quotations / sales orders">
            Hidden: Sales
          </span>
        )}
      </span>
    )),
  },
  ];
}

// ─── ProductListPage ────────────────────────────────────────────────────────

function ProductListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const columns = useMemo(
    // A draft is an unfinished wizard entry, so opening it resumes the wizard
    // where it left off rather than dropping the user into the edit form.
    // A variant row opens its PARENT: names, category and tax are owned there,
    // so that is the page where a change is actually made.
    () => makeColumns(currency, (row) => navigate(
      row.is_draft
        ? `/products/new?draft=${row.id}`
        : `/products/${row.parent_product_id ?? row.id}`,
    )),
    [currency, navigate],
  );
  const profile = useAuthStore((s) => s.profile);
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const searchRef = useRef<HTMLInputElement>(null);
  const userRoleLevel = getUserRoleLevel(profile?.role);

  // Can this user perform admin actions?
  const canBulkEdit = userRoleLevel >= ROLE_LEVELS.MANAGER;
  // QA CS4-005: category is an owner-only master field (OD11), so the bulk
  // Change Category action is offered to an OWNER only.
  const canBulkChangeCategory = userRoleLevel >= ROLE_LEVELS.OWNER;

  const page = Number(searchParams.get('page') ?? '1');
  const searchQuery = searchParams.get('search') ?? '';
  const categoryFilter = searchParams.get('category') ?? '';
  const statusFilter = searchParams.get('status') ?? '';
  const locationFilter = searchParams.get('location') ?? '';
  const variantFilter = searchParams.get('variants') ?? '';
  const typeFilter = searchParams.get('type') ?? '';
  const sortParam = searchParams.get('sort') ?? '';
  const orderParam = searchParams.get('order') ?? '';
  const viewMode = (searchParams.get('view') ?? 'list') as 'grid' | 'list';

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // An empty result while filters are active means "nothing matched", NOT
  // "no products exist" — the Category dropdown also lists categories that no
  // product uses yet (it merges the categories table), so landing on a
  // zero-result filter is easy and the first-run copy is actively misleading.
  const hasActiveFilters = Boolean(
    searchQuery || categoryFilter || statusFilter || locationFilter || variantFilter || typeFilter,
  );

  function clearAllFilters() {
    const params = new URLSearchParams(searchParams);
    for (const key of ['search', 'category', 'status', 'location', 'variants', 'type', 'page']) {
      params.delete(key);
    }
    setSearchInput('');
    setSearchParams(params);
  }

  const emptyStateTitle = hasActiveFilters
    ? 'No products match these filters'
    : 'No products yet';
  const emptyStateDescription = hasActiveFilters
    ? 'No product uses this combination. Clear the filters to see all products.'
    : 'Add your first product to start selling';
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');

  // Debounced search — 300ms.
  // Guard: this effect also re-runs whenever `searchParams` changes (it is in
  // the deps), e.g. when the user paginates. Without the early-return below it
  // would then force `page=1`, snapping the user back off page 2. Only rewrite
  // the URL (and reset the page) when the debounced input actually differs from
  // the search term already in the URL.
  useEffect(() => {
    const timer = setTimeout(() => {
      const current = searchParams.get('search') ?? '';
      if (searchInput === current) return;
      const params = new URLSearchParams(searchParams);
      if (searchInput) params.set('search', searchInput);
      else params.delete('search');
      params.set('page', '1');
      setSearchParams(params);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchParams, setSearchParams]);

  // Keyboard shortcuts per spec: /, n, g+l, g+g
  useEffect(() => {
    let gPressed = false;
    let gTimer: ReturnType<typeof setTimeout>;

    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      if (e.key === '/' && !isInput) {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }

      if (isInput) return;

      if (e.key === 'n') {
        e.preventDefault();
        navigate('/products/new');
        return;
      }

      if (e.key === 'g') {
        if (gPressed) return;
        gPressed = true;
        gTimer = setTimeout(() => { gPressed = false; }, 500);
        return;
      }

      if (gPressed) {
        gPressed = false;
        clearTimeout(gTimer);
        if (e.key === 'l') {
          e.preventDefault();
          setViewMode('list');
        } else if (e.key === 'g') {
          e.preventDefault();
          setViewMode('grid');
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, searchParams, setSearchParams]);

  // Fetch categories: the persisted categories table as an indented tree
  // (C13), so newly-created (item-less) categories like "Breakfast" still
  // appear, then the names products use that the table lacks.
  const { data: categoryFilterData } = useCategoryFilterOptions();

  // Bulk Change Category sets a product's category: category master rows only (C03).
  const { data: masterCategories } = useCategoryMasterNames();

  const categoryOptions = categoryFilterData ?? [{ value: '', label: 'All Categories' }];

  // Fetch locations for admin location filter (spec 3.13)
  const { data: locData } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: () => locationService.list({ limit: 100 }),
    staleTime: 60_000,
  });

  const locationOptions = [
    { value: '', label: 'All Locations' },
    ...(locData?.data ?? []).map((l) => ({ value: l.id, label: l.name })),
  ];

  // Fetch products
  const effectiveLocationId = locationFilter || selectedLocationId;
  // "All Locations" lists each product once, from the master catalogue
  // (master catalogue plan §11); one store keeps its own rows and stock.
  const masterView = !effectiveLocationId;
  const { data, isLoading: storeLoading, error: storeError } = useQuery({
    queryKey: ['products', page, searchQuery, categoryFilter, statusFilter, typeFilter, variantFilter, effectiveLocationId, sortParam, orderParam],
    queryFn: () =>
      productService.list({
        page,
        limit: 20,
        search: searchQuery || undefined,
        category: categoryFilter || undefined,
        stock_status: statusFilter || undefined,
        product_type: typeFilter || undefined,
        has_variants: variantFilter ? String(variantFilter === 'with') : undefined,
        // Parents only (FR-PROD-007): a parent's variants live on its Variants tab,
        // so they don't crowd other products off the page.
        include_variants: 'false',
        location_id: effectiveLocationId,
        sort: sortParam || undefined,
        order: orderParam || undefined,
      }),
    staleTime: 15_000,
    enabled: !masterView,
  });

  const { data: masterData, isLoading: masterLoading, error: masterError } = useQuery({
    queryKey: ['product-masters', 'list', 'all-locations', page, searchQuery, categoryFilter, statusFilter, typeFilter, variantFilter, sortParam, orderParam],
    queryFn: () =>
      productMasterService.list({
        page,
        limit: 20,
        search: searchQuery || undefined,
        category: categoryFilter || undefined,
        // Parents only, as the store list: a variant opens through its parent.
        is_variant: 'false',
        // C13 (D-G2): the same filters and sort as one store's list.
        stock_status: statusFilter || undefined,
        product_type: typeFilter || undefined,
        has_variants: variantFilter ? String(variantFilter === 'with') : undefined,
        sort: sortParam || undefined,
        order: orderParam || undefined,
      }),
    staleTime: 15_000,
    enabled: masterView,
  });

  const { data: masterSummary } = useQuery({
    queryKey: ['product-masters', 'summary'],
    queryFn: () => productMasterService.summary(),
    staleTime: 15_000,
    enabled: masterView,
  });

  const masterColumns = useMemo(
    () =>
      makeMasterColumns({
        currency,
        sortable: true,
        storeTotal: masterSummary?.stores ?? 0,
        onOpen: (row) =>
          navigate(masterProductHref(row) ?? `/products/masters?search=${encodeURIComponent(row.sku)}`),
      }),
    [currency, masterSummary?.stores, navigate],
  );

  const isLoading = masterView ? masterLoading : storeLoading;
  const error = masterView ? masterError : storeError;

  const handleSort = useCallback(
    (column: string, direction: 'asc' | 'desc') => {
      const params = new URLSearchParams(searchParams);
      if (sortParam === column && orderParam === 'desc') {
        params.delete('sort');
        params.delete('order');
      } else {
        params.set('sort', column);
        params.set('order', direction);
      }
      params.set('page', '1');
      setSearchParams(params);
    },
    [searchParams, setSearchParams, sortParam, orderParam],
  );
  // The master table's column keys in the store list's sort names, so the URL
  // means the same thing in both views (C13).
  const handleMasterSort = useCallback(
    (column: string, direction: 'asc' | 'desc') =>
      handleSort(MASTER_SORT_KEY[column] ?? column, direction),
    [handleSort],
  );

  // Bulk mutations
  const deactivateMutation = useMutation({
    mutationFn: (ids: string[]) =>
      runBulkProductUpdate(ids, (id) => productService.update(id, { is_active: false })),
    onSuccess: ({ succeeded, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // Keep the failures selected so they can be inspected or retried.
      setSelectedRows(failed);
      if (failed.length === 0) {
        toast({ title: 'Products deactivated', variant: 'success' });
      } else if (succeeded.length === 0) {
        toast({ title: `Could not deactivate ${failed.length} product(s)`, variant: 'error' });
      } else {
        toast({
          title: `${succeeded.length} deactivated, ${failed.length} failed`,
          description: 'The products that failed are still selected.',
          variant: 'warning',
        });
      }
    },
  });

  const changeCategoryMutation = useMutation({
    mutationFn: ({ ids, category }: { ids: string[]; category: string }) =>
      runBulkProductUpdate(ids, (id) => productService.update(id, { category })),
    onSuccess: ({ succeeded, failed, firstError }) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // Keep the refused products selected so they can be inspected or retried.
      setSelectedRows(failed);
      setCategoryPickerOpen(false);
      if (failed.length === 0) {
        toast({ title: 'Category updated', variant: 'success' });
      } else {
        const reason = apiErrorMessage(firstError, 'The category could not be changed.');
        toast({
          title: succeeded.length === 0
            ? `Could not change the category of ${failed.length} product(s)`
            : `${succeeded.length} changed, ${failed.length} refused`,
          description: reason,
          variant: succeeded.length === 0 ? 'error' : 'warning',
        });
      }
    },
  });

  /** "created_at:desc" -> sort=created_at & order=desc; empty clears both. */
  function setSortOption(value: string) {
    const params = new URLSearchParams(searchParams);
    if (!value) {
      params.delete('sort');
      params.delete('order');
    } else {
      const [col, dir] = value.split(':');
      params.set('sort', col);
      params.set('order', dir ?? 'asc');
    }
    params.delete('page');
    setSearchParams(params);
  }

  function setFilter(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  }

  function setViewMode(mode: 'grid' | 'list') {
    const params = new URLSearchParams(searchParams);
    params.set('view', mode);
    setSearchParams(params);
  }

  const handleExportCsv = useCallback(async () => {
    if (!data?.data?.length) return;
    let all: ProductRow[];
    try {
      all = await fetchAllProductsForExport(
        (p) => productService.list(p) as Promise<{ data: ProductRow[]; meta: { totalPages: number } }>,
        {
          search: searchQuery || undefined,
          category: categoryFilter || undefined,
          stock_status: statusFilter || undefined,
          product_type: typeFilter || undefined,
          has_variants: variantFilter ? String(variantFilter === 'with') : undefined,
          include_variants: 'false',
          location_id: effectiveLocationId,
          sort: sortParam || undefined,
          order: orderParam || undefined,
        },
      );
    } catch {
      toast({ title: 'Export failed', description: 'Could not load the full catalogue. Please try again.', variant: 'error' });
      return;
    }
    const blob = new Blob([buildProductExportCsv(all, currency)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, currency, searchQuery, categoryFilter, statusFilter, typeFilter, variantFilter, effectiveLocationId, sortParam, orderParam, toast]);

  // All Locations: the whole master catalogue in the import's format, plus
  // each product's total stock (C14) — the Master Catalogue page's file.
  const handleMasterExport = useCallback(async () => {
    try {
      const { blob, filename } = await productMasterService.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? 'product-masters.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: (err as Error).message || 'Export failed', variant: 'error' });
    }
  }, [toast]);

  // `has_variants` is a server-side filter (part of the same WHERE that drives
  // LIMIT/OFFSET and the count), so the page is already narrowed — filtering
  // again here would strand matches on later pages and desync the total.
  const products = (data?.data ?? []) as ProductRow[];
  const masters = masterData?.data ?? [];
  const totalPages = (masterView ? masterData : data)?.meta?.totalPages ?? 1;
  const totalItems = (masterView ? masterData : data)?.meta?.total ?? 0;

  // Error state
  if (error) {
    return (
      <PageContainer title="Products" breadcrumbs={[{ label: 'Products' }]}>
        <div className={cn(GLASS, 'p-6 text-center')}>
          <p className="text-sm text-danger-600 dark:text-danger-400">
            Failed to load products. Please try refreshing.
          </p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => queryClient.invalidateQueries({ queryKey: ['products'] })}>
            Retry
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Filter panel content (shared between desktop inline and mobile Drawer)
  const filterControls = (
    <>
      <div className="w-full sm:w-44">
        <Select
          options={categoryOptions}
          value={categoryFilter}
          onChange={(v) => setFilter('category', typeof v === 'string' ? v : '')}
          placeholder="Category"
        />
      </div>
      <div className="w-full sm:w-40">
        <Select
          options={STOCK_STATUS_OPTIONS as unknown as { value: string; label: string }[]}
          value={statusFilter}
          onChange={(v) => setFilter('status', typeof v === 'string' ? v : '')}
          placeholder="Stock Status"
        />
      </div>
      <div className="w-full sm:w-44">
        <Select
          options={SORT_OPTIONS}
          value={sortParam ? `${sortParam}:${orderParam || 'asc'}` : ''}
          onChange={(v) => setSortOption(typeof v === 'string' ? v : '')}
          placeholder="Sort by"
        />
      </div>
      <div className="w-full sm:w-36">
        <Select
          options={PRODUCT_TYPE_FILTER_OPTIONS}
          value={typeFilter}
          onChange={(v) => setFilter('type', typeof v === 'string' ? v : '')}
          placeholder="Type"
        />
      </div>
      <div className="w-full sm:w-40">
        <Select
          options={VARIANT_FILTER_OPTIONS}
          value={variantFilter}
          onChange={(v) => setFilter('variants', typeof v === 'string' ? v : '')}
          placeholder="Variants"
        />
      </div>
      <div className="w-full sm:w-44">
        <Select
          options={locationOptions}
          value={locationFilter}
          onChange={(v) => setFilter('location', typeof v === 'string' ? v : '')}
          placeholder="Location"
        />
      </div>
    </>
  );

  return (
    <PageContainer
      title="Products"
      subtitle="Manage your product catalog across all locations"
      breadcrumbs={[{ label: 'Products' }]}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setReorderOpen(true)}>
            Reorder
          </Button>
          <Button variant="secondary" onClick={() => navigate('/products/variant-attributes')}>
            Variant Attributes
          </Button>
          <Button variant="secondary" onClick={() => navigate('/products/import')}>
            Import CSV
          </Button>
          <Button variant="primary" onClick={() => navigate('/products/new')}>
            + Add Product
          </Button>
        </div>
      }
    >
      {/* ─── Filter Bar (Glass) ──────────────────────────────────────── */}
      <div className={cn(GLASS_FILTER, 'p-3 mb-6')}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="w-full sm:w-72">
            <Input
              ref={searchRef}
              variant="search"
              placeholder="Search products…"
              value={searchInput}
              onChange={(v) => setSearchInput(typeof v === 'string' ? v : String(v))}
              aria-label="Search products"
            />
          </div>

          {/* Desktop filters */}
          <div className="hidden sm:contents">
            {filterControls}
          </div>

          {/* Mobile filter button */}
          <div className="sm:hidden">
            <Button variant="ghost" size="sm" onClick={() => setMobileFilterOpen(true)} aria-label="Open filters">
              <FilterIcon />
              <span className="ml-1.5">Filters</span>
            </Button>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {/* Bulk Actions (desktop, role-gated) */}
            {canBulkEdit && !masterView && selectedRows.length > 0 && (
              <div className="hidden sm:block">
                <Dropdown
                  trigger={
                    <Button variant="secondary" size="sm">
                      Bulk Actions ({selectedRows.length})
                    </Button>
                  }
                  items={[
                    {
                      label: 'Deactivate Selected',
                      danger: true,
                      onClick: () => deactivateMutation.mutate(selectedRows),
                    },
                    ...(canBulkChangeCategory ? [{
                      label: 'Change Category',
                      onClick: () => {
                        if (categoryOptions.length > 1) {
                          // The user picks the target category in the modal below.
                          setBulkCategory('');
                          setCategoryPickerOpen(true);
                        } else {
                          toast({ title: 'No categories available', variant: 'warning' });
                        }
                      },
                    }] : []),
                  ]}
                />
              </div>
            )}

            {/* View mode toggle — icon-based */}
            {!masterView && (
            <div className="flex rounded-lg overflow-hidden" role="radiogroup" aria-label="View mode">
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === 'grid'}
                aria-label="Grid view"
                className={cn(
                  'flex items-center justify-center h-8 w-8 transition-colors duration-150',
                  viewMode === 'grid'
                    ? 'bg-teal-600 text-white'
                    : 'bg-white/80 dark:bg-neutral-800/80 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300',
                )}
                onClick={() => setViewMode('grid')}
              >
                <GridIcon />
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === 'list'}
                aria-label="List view"
                className={cn(
                  'flex items-center justify-center h-8 w-8 transition-colors duration-150',
                  viewMode === 'list'
                    ? 'bg-teal-600 text-white'
                    : 'bg-white/80 dark:bg-neutral-800/80 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300',
                )}
                onClick={() => setViewMode('list')}
              >
                <ListIcon />
              </button>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <Drawer
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        position="bottom"
        title="Filters"
      >
        <div className="space-y-4 p-4">
          {filterControls}
          <Button variant="primary" className="w-full" onClick={() => setMobileFilterOpen(false)}>
            Apply Filters
          </Button>
        </div>
      </Drawer>

      {masterView && (
        <p className="mb-4 rounded-lg bg-teal-50 px-4 py-2 text-sm text-teal-800 dark:bg-teal-900/20 dark:text-teal-200 animate-fade-in">
          Each product is listed once for all your stores. Its stock is all your stores added up. Point
          at it to see each store. Open the{' '}
          <button
            type="button"
            className="font-semibold underline hover:text-teal-600 dark:hover:text-teal-300"
            onClick={() => navigate('/products/masters')}
          >
            Master Catalogue
          </button>{' '}
          to choose which stores sell what.
        </p>
      )}

      {/* ─── Content ─────────────────────────────────────────────────── */}
      {masterView ? (
        <DataTable<ProductMasterListItem>
          columns={masterColumns}
          data={masters}
          loading={isLoading}
          totalCount={totalItems}
          currentPage={page}
          pageSize={20}
          onSort={handleMasterSort}
          onPageChange={(p) => {
            const params = new URLSearchParams(searchParams);
            params.set('page', String(p));
            setSearchParams(params);
          }}
          emptyState={{
            icon: <PackageIcon className="h-16 w-16" />,
            title: emptyStateTitle,
            description: emptyStateDescription,
            action: hasActiveFilters ? (
              <Button variant="secondary" onClick={clearAllFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={() => navigate('/products/new')}>
                Add Product
              </Button>
            ),
          }}
          onExport={handleMasterExport}
          stickyHeader
        />
      ) : isLoading ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} variant="rect" height={220} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} variant="rect" height={56} />
            ))}
          </div>
        )
      ) : viewMode === 'list' ? (
        <DataTable<ProductRow>
          columns={columns}
          data={products}
          loading={isLoading}
          totalCount={totalItems}
          currentPage={page}
          pageSize={20}
          onPageChange={(p) => {
            const params = new URLSearchParams(searchParams);
            params.set('page', String(p));
            setSearchParams(params);
          }}
          onSort={handleSort}
          selectable={canBulkEdit}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          bulkActions={canBulkEdit ? [
            { label: 'Deactivate', onClick: (ids) => deactivateMutation.mutate(ids) },
          ] : undefined}
          emptyState={{
            icon: <PackageIcon className="h-16 w-16" />,
            title: emptyStateTitle,
            description: emptyStateDescription,
            action: hasActiveFilters ? (
              <Button variant="secondary" onClick={clearAllFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={() => navigate('/products/new')}>
                Add Product
              </Button>
            ),
          }}
          onExport={handleExportCsv}
          stickyHeader
        />
      ) : (
        <>
          {products.length === 0 ? (
            <EmptyState
              icon={<PackageIcon className="h-full w-full" />}
              title={emptyStateTitle}
              description={emptyStateDescription}
              action={hasActiveFilters
                ? { label: 'Clear Filters', onClick: clearAllFilters }
                : { label: 'Add Product', onClick: () => navigate('/products/new') }}
              variant="page"
            />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${product.name}`}
                    className={cn(GLASS_INTERACTIVE, 'overflow-hidden focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2')}
                    onClick={() => navigate(product.is_draft ? `/products/new?draft=${product.id}` : `/products/${product.parent_product_id ?? product.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(product.is_draft ? `/products/new?draft=${product.id}` : `/products/${product.parent_product_id ?? product.id}`); } }}
                  >
                    <div className="aspect-square overflow-hidden">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/10">
                          <PackageIcon className="h-12 w-12 text-teal-300 dark:text-teal-700" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{product.name}</p>
                      <p className="text-xs text-neutral-400 dark:text-neutral-500 tabular-nums truncate">{product.sku}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                          {formatPaise(product.price, currency)}
                        </span>
                        <StockLevelIndicator
                          currentStock={product.stock_quantity}
                          reorderLevel={product.low_stock_threshold ?? undefined}
                          variant="badge"
                          size="sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Grid Pagination — uses Pagination component */}
              {totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={20}
                    onPageChange={(p) => {
                      const params = new URLSearchParams(searchParams);
                      params.set('page', String(p));
                      setSearchParams(params);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      <Modal
        open={categoryPickerOpen}
        onClose={() => setCategoryPickerOpen(false)}
        title={`Change category for ${selectedRows.length} product(s)`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCategoryPickerOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!bulkCategory || changeCategoryMutation.isPending}
              onClick={() =>
                changeCategoryMutation.mutate({ ids: selectedRows, category: bulkCategory })
              }
            >
              Apply
            </Button>
          </>
        }
      >
        <Select
          label="New category"
          placeholder="Select a category"
          options={(masterCategories ?? []).map((c) => ({ value: c, label: c }))}
          value={bulkCategory}
          onChange={(v) => setBulkCategory(typeof v === 'string' ? v : '')}
        />
      </Modal>

      <ReorderModal
        open={reorderOpen}
        onClose={() => setReorderOpen(false)}
        locationId={effectiveLocationId ?? undefined}
        currency={currency}
      />
    </PageContainer>
  );
}

export function ProductListPage() {
  return (
    <ErrorBoundary>
      <ProductListContent />
    </ErrorBoundary>
  );
}
